import { useState } from 'react'

import { useReactiveVar } from '@apollo/client'
import { ComboButton, Icon, PrimaryButton, Tooltip } from '@joor/design-system'
import { useFlags } from '@joor/launchdarkly-react-client-sdk'
import { useSelector } from 'react-redux'
import { generatePath, useNavigate } from 'react-router-dom'
import styled from 'styled-components'

import {
  OrderCheckMinimums,
  OrderReviewGigaQueryDocument,
  OrderStatus,
} from '__generated__/atlas-types'

import { useUserAnalytics } from 'utils/sdks/userAnalytics'
import {
  fromGlobalId,
  relayConnectionToArray,
} from 'utils/transformations/graphql'

import { useRenderToasters } from 'hooks/useRenderToasters'
import {
  isAccountTypeBrand as isAccountTypeBrandSelector,
  isApproveOrderRestricted as isApproveOrderRestrictedSelector,
  isLiteRetailer as isLiteRetailerSelector,
  isPlaceOrderUnderMinRestricted as isPlaceOrderUnderMinRestrictedSelector,
  isProBrand as isProBrandSelector,
} from 'store/currentUser/selectors'

import DuplicateOrdersModal from 'modals/OrderList/DuplicateOrdersModal'
import { ORDER_MODIFY } from 'routes/paths'

import { getOptionsByOrderStatus } from '../../header.configuration'
import {
  CollectionNode,
  OptionType,
  OrderCollections,
} from '../../header.types'
import ApprovedOrderRequestPaymentModal from '../modals/ApprovedOrderRequestPaymentModal'
import CancelOrderModal from '../modals/CancelOrderModal'
import OrderErrorModal from '../modals/OrderErrorModal'
import OrderMinRequirementsModal, {
  shouldModalOpen as shouldOrderMinRequirementsModalOpen,
} from '../modals/OrderMinRequirementsModal'
import { useChecksMinimums } from './statusDropdown.queries'
import { duplicateOrders } from './statusDropdown.requests'
import withModal from 'containers/ModalHandler/ModalHandler.component'
import { ChangeStatusModal } from 'features/Orders/Modals/ChangeStatusModal/ChangeStatusModal.component'
import { shouldModalOpen as shouldChangeStatusModalOpen } from 'features/Orders/Modals/ChangeStatusModal/ChangeStatusModal.utils'
import { StatusDropdownIds } from 'features/Orders/Review/review.ids'
import { useOrderHasRemainingBalance } from 'features/Orders/Review/review.queries'
import { orderReviewPartialErrorVar } from 'features/Orders/Review/review.store'
import { useBulkUpdateOrderStatus } from 'features/Orders/orders.hooks'

const StyledComboButton = styled(ComboButton)`
  &:first-child {
    min-width: 184px;
    justify-content: flex-start;
    margin-right: 1px;
    font-size: var(--size-font-small);
  }
`

const StyledButton = styled(PrimaryButton)`
  span {
    min-width: 135px;
    font-size: var(--size-font-small);
  }
`

const ComboButtonWrapper = styled.div`
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: center;
  padding: 0 20px;

  span div {
    width: calc(184px + 33px);
    z-index: 2;
  }
`

const CancelOrderLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: var(--color-error-700);
  > span {
    display: flex;
  }
  svg {
    --main-color: var(--color-error-700);
  }
`

const getArchivedCollectionsDuplicateTooltip = (
  archivedNames: string[],
): string => {
  const trimmed = archivedNames.map((n) => n.trim()).filter(Boolean)
  if (trimmed.length === 0) {
    return 'This order can not be duplicated, the linesheet was archived'
  }
  const escapeQuotes = (name: string) => name.replace(/"/g, "'")
  if (trimmed.length === 1) {
    return `This order can not be duplicated, the linesheet "${escapeQuotes(trimmed[0])}" was archived`
  }
  const listed = trimmed.map((n) => `"${escapeQuotes(n)}"`).join(', ')
  return `This order can not be duplicated, these linesheets were archived: ${listed}`
}

type Props = {
  order: {
    id: string
    webOrderLookupId: string
    status: OrderStatus
    retailerProfile: {
      id: string
      name: string
    }
    checkMinimums: OrderCheckMinimums
    isOrphan: boolean
    collections?: OrderCollections
  }
  openDynamicModal: (value?: unknown) => void
  error: boolean
  disabled: boolean
}

const StatusDropdown = ({ order, openDynamicModal, disabled }: Props) => {
  const {
    id: orderId,
    webOrderLookupId,
    status: orderStatus,
    retailerProfile,
    isOrphan,
    collections,
  } = order

  const collectionNodes = relayConnectionToArray(
    collections,
  ) as CollectionNode[]
  const hasArchivedCollection = collectionNodes.some(
    (collection) => collection?.archived,
  )
  const archivedCollectionNames = collectionNodes
    .filter((collection) => collection?.archived)
    .map((collection) =>
      typeof collection?.name === 'string' ? collection.name.trim() : '',
    )
    .filter((name): name is string => name.length > 0)
  const archivedCollectionTooltipMessage = hasArchivedCollection
    ? getArchivedCollectionsDuplicateTooltip(archivedCollectionNames)
    : ''

  const { joorPayUseReach } = useFlags()

  const navigate = useNavigate()
  const { areOrderMinimumsMet, loadingCheckMinimums } = useChecksMinimums()
  const { hasRemainingBalance, loading: loadingHasRemainingBalance } =
    useOrderHasRemainingBalance()

  const isApproveOrderRestricted = useSelector(isApproveOrderRestrictedSelector)
  const isPlaceOrderUnderMinRestricted = useSelector(
    isPlaceOrderUnderMinRestrictedSelector,
  )
  const isAccountTypeBrand = useSelector(isAccountTypeBrandSelector)
  const [isCancelOrderModalOpen, setCancelOrderModalOpen] = useState(false)
  const [isOrderErrorModalOpen, setOrderErrorModalOpen] = useState(false)
  const [isChangeStatusModalOpen, setIsChangeStatusModalOpen] = useState(false)
  const [isOrderMinRequirementsModalOpen, setIsOrderMinRequirementsModalOpen] =
    useState(false)
  const isLiteRetailer = useSelector(isLiteRetailerSelector)
  const isProBrand = useSelector(isProBrandSelector)
  const [newStatus, setNewStatus] = useState<OrderStatus | undefined>()
  const { trackEvent } = useUserAnalytics()

  const [isDuplicatingOrder, setDuplicatingOrder] = useState(false)

  const { renderErrorToast, renderSuccessToast } = useRenderToasters()
  const isTherePriceTypeError = useReactiveVar(
    orderReviewPartialErrorVar,
  )?.priceType

  const [isModalOpen, setIsModalOpen] = useState(false)

  const handleDuplicateOrderError = () =>
    renderErrorToast({
      title: 'Error',
      description: 'Failed to duplicate this Order.',
    })

  const { bulkUpdateOrderStatus, isUpdating: isUpdatingOrderStatus } =
    useBulkUpdateOrderStatus({
      onError: () => {
        renderErrorToast({
          title: 'Error',
          description:
            'There was an error updating the status. Try again later.',
        })
      },
      onCompleted: ({ bulkUpdateOrderStatus: { errors, updatedOrderIds } }) => {
        if (errors.length) {
          renderErrorToast({
            title: 'Error',
            description: errors.map((error) => <li>{error.message}</li>),
            shouldCloseManually: true,
          })
        } else {
          renderSuccessToast({
            title: 'Success',
            description: `${updatedOrderIds.length} order(s) have been updated`,
          })
        }
      },
      refetchQueries: [OrderReviewGigaQueryDocument],
    })

  const updateOrderStatusTo = async (newOrderStatus: OrderStatus) => {
    if (isTherePriceTypeError) {
      newOrderStatus === OrderStatus.CANCELLED
        ? setCancelOrderModalOpen(true)
        : setOrderErrorModalOpen(true)
    } else {
      if (
        shouldOrderMinRequirementsModalOpen({
          isProBrand,
          isLiteRetailer,
          newStatus: newOrderStatus,
          areMinimumsMet: areOrderMinimumsMet,
        })
      ) {
        setIsOrderMinRequirementsModalOpen(true)
        setNewStatus(newOrderStatus)
      } else if (
        shouldChangeStatusModalOpen(isProBrand, isLiteRetailer, newOrderStatus)
      ) {
        setIsChangeStatusModalOpen(true)
        setNewStatus(newOrderStatus)
      } else {
        await bulkUpdateOrderStatus({
          orderIds: [orderId],
          status: newOrderStatus,
        })
      }
    }
  }

  const duplicateOrder = () => {
    const { id, name } = retailerProfile

    const data = {
      webOrderLookupIds: [webOrderLookupId],
      retailerProfile: {
        id,
        displayName: name,
      },
    }

    setDuplicatingOrder(true)
    duplicateOrders(data)
      .then((response) => {
        if (response.data?.success) {
          renderSuccessToast({
            title: 'Order duplicated',
            description: 'Order has been successfully duplicated.',
          })
        } else {
          handleDuplicateOrderError()
        }
      })
      .catch(() => {
        handleDuplicateOrderError()
      })
      .finally(() => setDuplicatingOrder(false))
  }

  const openDuplicateOrdersModal = () => {
    openDynamicModal({
      component: DuplicateOrdersModal,
      selectedOrders: [
        {
          ...order,
          webOrderLookupId: fromGlobalId(webOrderLookupId).id,
        },
      ],
      handleResetOrder: () => {},
      redirectOnSuccess: false,
      renderActionsToasterMessage: () => {
        renderSuccessToast({
          title: 'Order duplicated',
          description: 'Order has been successfully duplicated.',
        })
      },
      shouldUpdateCart: true,
    })
  }

  const allDropdownOptions = [
    {
      id: StatusDropdownIds.Approve,
      label: 'Approve',
      onClick: () => {
        updateOrderStatusTo(OrderStatus.APPROVED)
      },
    },
    {
      id: StatusDropdownIds.SetToPending,
      label: 'Set to Pending',
      onClick: () => {
        updateOrderStatusTo(OrderStatus.PENDING)
      },
    },
    {
      id: StatusDropdownIds.SetToNotes,
      label: 'Set to Notes',
      onClick: () => {
        updateOrderStatusTo(OrderStatus.NOTES)
      },
    },
    {
      id: StatusDropdownIds.MarkAsShipped,
      label: 'Mark as Shipped',
      onClick: () => {
        updateOrderStatusTo(OrderStatus.SHIPPED)
      },
    },
    {
      id: StatusDropdownIds.DuplicateOrder,
      label: 'Duplicate Order',
      onClick: () => {
        if (isTherePriceTypeError) {
          setOrderErrorModalOpen(true)
        } else {
          isAccountTypeBrand ? openDuplicateOrdersModal() : duplicateOrder()
        }
      },
    },
    {
      id: StatusDropdownIds.Share,
      label: 'Share',
      onClick: () => {
        updateOrderStatusTo(OrderStatus.NOTES)
      },
    },
    {
      id: StatusDropdownIds.SubmitForApproval,
      label: 'Submit for Approval',
      onClick: () => {
        updateOrderStatusTo(OrderStatus.PENDING)
      },
    },
    {
      id: StatusDropdownIds.EditOrder,
      label: 'Edit Order',
      onClick: () => {
        updateOrderStatusTo(OrderStatus.NOTES)
      },
    },
    {
      id: StatusDropdownIds.CancelOrder,
      label: (
        <CancelOrderLabel>
          <Icon iconSize="small" iconName="no" plain />
          Cancel Order
        </CancelOrderLabel>
      ),
      onClick: () => {
        updateOrderStatusTo(OrderStatus.CANCELLED)
      },
    },
    {
      id: StatusDropdownIds.RequestPayment,
      label: 'Request Payment',
      onClick: () => {
        setIsModalOpen(true)
      },
    },
    {
      id: StatusDropdownIds.PayNow,
      label: 'Pay now',
      onClick: () => {
        navigate(
          {
            pathname: window.location.pathname,
            search: new URLSearchParams({ tab: 'pay' }).toString(),
          },
          { replace: true },
        )
      },
    },
  ]

  const { defaultOptionId, optionIds } = getOptionsByOrderStatus(
    orderStatus,
    isApproveOrderRestricted,
    isAccountTypeBrand,
    isOrphan,
    hasRemainingBalance && !loadingHasRemainingBalance,
    joorPayUseReach,
  )

  const myDropdownOptions = allDropdownOptions.filter(
    (option: OptionType) =>
      option.id !== defaultOptionId &&
      optionIds.includes(option.id) &&
      !(
        hasArchivedCollection && option.id === StatusDropdownIds.DuplicateOrder
      ),
  )

  const defaultOption = allDropdownOptions.find(
    (option) => option.id === defaultOptionId,
  )

  const isDuplicateOrderPrimary =
    defaultOption?.id === StatusDropdownIds.DuplicateOrder
  const blockDuplicateOrder = hasArchivedCollection && isDuplicateOrderPrimary

  const handleMinimumsModalContinue = async () => {
    setIsOrderMinRequirementsModalOpen(false)
    setIsChangeStatusModalOpen(true)
  }

  const handleEditQuantities = () => {
    const webId = fromGlobalId(webOrderLookupId).id
    const id = fromGlobalId(orderId).id
    if (!webId || !id) {
      return
    }

    const modifyLink = generatePath(ORDER_MODIFY, {
      orderId: webId,
    })
    const redirectToModify = `${modifyLink}?order_id=${id}`
    setIsOrderMinRequirementsModalOpen(false)
    navigate(redirectToModify)
  }

  const isDisabled =
    isUpdatingOrderStatus ||
    isDuplicatingOrder ||
    loadingCheckMinimums ||
    disabled ||
    blockDuplicateOrder

  const primaryButton =
    myDropdownOptions.length > 0 ? (
      <StyledComboButton
        onClick={() => {
          defaultOption?.onClick()
        }}
        options={myDropdownOptions}
        disabled={isDisabled}
        id={defaultOption?.id}
      >
        {defaultOption?.label || ''}
      </StyledComboButton>
    ) : (
      <StyledButton
        onClick={() => {
          defaultOption?.onClick()
          trackEvent('Order_Review_Status_CTA_(Primary)')
        }}
        disabled={isDisabled}
        id={defaultOption?.id}
      >
        {defaultOption?.label || ''}
      </StyledButton>
    )

  return (
    <>
      <ComboButtonWrapper>
        {blockDuplicateOrder ? (
          <Tooltip
            content={archivedCollectionTooltipMessage}
            defaultPlacement="left"
            variant="error"
          >
            {primaryButton}
          </Tooltip>
        ) : (
          primaryButton
        )}
      </ComboButtonWrapper>
      <CancelOrderModal
        isOpen={isCancelOrderModalOpen}
        setOpen={setCancelOrderModalOpen}
        action={() => {
          updateOrderStatusTo(OrderStatus.CANCELLED)
          setCancelOrderModalOpen(false)
        }}
      />
      <OrderErrorModal
        isOpen={isOrderErrorModalOpen}
        setOpen={setOrderErrorModalOpen}
      />
      <ChangeStatusModal
        isOpen={isChangeStatusModalOpen}
        setIsOpen={setIsChangeStatusModalOpen}
        status={newStatus}
        primaryBtnAction={bulkUpdateOrderStatus}
        isLoading={isUpdatingOrderStatus}
      />
      {isOrderMinRequirementsModalOpen && (
        <OrderMinRequirementsModal
          onContinue={handleMinimumsModalContinue}
          onEditQuantities={handleEditQuantities}
          onClose={() => setIsOrderMinRequirementsModalOpen(false)}
          isLoading={isUpdatingOrderStatus}
          applyRestrictions={Boolean(isPlaceOrderUnderMinRestricted)}
          checkMinimums={order?.checkMinimums}
        />
      )}
      {isModalOpen && (
        <ApprovedOrderRequestPaymentModal
          isModalOpen={isModalOpen}
          setIsModalOpen={setIsModalOpen}
        />
      )}
    </>
  )
}

export default withModal(StatusDropdown)
